import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormTextField } from '@uncinc/uncinc-react-kitchen-sink';

import PostalCodeAutocomplete from '../Form/PostalCodeAutocomplete';

import './style.scss';

/**
 * Shared address field block.
 *
 * Renders the common address inputs (organisation, name, postcode autocomplete,
 * street + house number, city) for the unified address book and for checkout
 * billing/delivery. Field names are namespaced by `prefix` ('' for delivery,
 * 'billing_' for billing) so one block serves every consumer. The
 * organisation-hidden-for-particulier rule lives here once (via showOrganisation).
 * Field order follows the design: organisation, name, postcode + house number
 * (paired, with an "inclusief toevoeging" hint), street, then city.
 *
 * @component
 */

interface AddressFieldsProps {
  /** Field-name prefix: '' for delivery, 'billing_' for billing. */
  prefix?: string;
  /** Whether to render the organisation field (hidden for particulier). */
  showOrganisation?: boolean;
  /** When true the organisation label drops the "(optioneel)" suffix. */
  organisationRequired?: boolean;
  /** Explicit organisation label; overrides the required/optional default. */
  organisationLabel?: string;
}

const AddressFields: React.FC<AddressFieldsProps> = ({
  prefix = '',
  showOrganisation = true,
  organisationRequired = false,
  organisationLabel,
}) => {
  const { t } = useTranslation();
  const field = (name: string): string => `${prefix}${name}`;
  const orgTitle =
    organisationLabel ??
    (organisationRequired
      ? t('address.organization_no_optional')
      : t('address.organization'));

  return (
    <>
      {showOrganisation && (
        <div className="row">
          <div className="col-12">
            <FormTextField title={orgTitle} fieldName={field('organization')} />
          </div>
        </div>
      )}
      <div className="row">
        <div className="col-12 col-sm-6">
          <FormTextField
            title={t('address.first_name')}
            fieldName={field('given_name')}
          />
        </div>
        <div className="col-12 col-sm-6">
          <FormTextField
            title={t('address.family_name')}
            fieldName={field('family_name')}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-sm-6">
          <PostalCodeAutocomplete
            title={t('address.postal_code')}
            fieldName={field('postal_code')}
            localityField={field('locality')}
            addressField={field('address_line1')}
          />
        </div>
        <div className="col-12 col-sm-6">
          <FormTextField
            title={t('address.address_line2')}
            fieldName={field('address_line2')}
          />
          <span className="AddressFields__hint">
            {t('address.house_number_hint')}
          </span>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <FormTextField
            title={t('address.address_line1')}
            fieldName={field('address_line1')}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <FormTextField
            title={t('address.locality')}
            fieldName={field('locality')}
          />
        </div>
      </div>
    </>
  );
};

export default AddressFields;
